'use client';

import {
    Button,
    Drawer,
    Heading,
    LoadingIndicator,
    ProductMediaBlock,
    SelectField,
    TextPassage,
} from '@petsmart-ui/sparky';
import { HTMLAttributes } from 'react';

import { ProductHit } from '@libs/algolia';
import { clsx } from '@libs/common-next';

import { Product } from './types';
import { useQuickAtc } from './useQuickAtc';
import './SetUpAutoshipButton.scss';

interface SetUpAutoshipButtonProps extends HTMLAttributes<HTMLButtonElement> {
    product: Product | ProductHit;
    className: string;
}

export function SetUpAutoshipButton({ product, className, ...props }: Readonly<SetUpAutoshipButtonProps>) {
    const { id, isCaseRequiredSTH, customCaseQuantity } = product;
    const {
        addedToCart,
        closeDrawer,
        frequencyItems,
        handleFormSubmit,
        isLoading,
        isDrawerOpen,
        onFrequencyChange,
        openDrawer,
        selectedFrequency,
    } = useQuickAtc({ id, isCaseRequiredSTH, customCaseQuantity });

    return (
        <>
            <div className={className}>
                <Button
                    type="submit"
                    variant="primary"
                    text={addedToCart ? 'Edit Autoship' : 'Set-Up Autoship'}
                    className={clsx(addedToCart && 'add-to-cart-button add-to-cart-button__green')}
                    iconName="autoship-outline"
                    data-testid="exp-quick-add-to-cart-open-drawer-button"
                    onClick={openDrawer}
                    {...props}
                />
            </div>
            <Drawer
                key={product.id}
                isActive={isDrawerOpen}
                onClose={closeDrawer}
                align="right"
                ariaLabelledBy="drawer-heading"
                className="opti-quick-atc-drawer"
                data-testid="opti-quick-atc-drawer"
            >
                <Drawer.Header>
                    <Heading
                        id="drawer-heading"
                        tagName="h2"
                        size="headline"
                        className="opti-quick-atc-drawer-drawer__header"
                    >
                        Set Up Autoship
                    </Heading>
                </Drawer.Header>
                <Drawer.Body>
                    {isLoading && (
                        <LoadingIndicator
                            data-testid="exp-quick-add-to-cart-loading"
                            className="transparent-overlay-loader"
                        />
                    )}
                    <TextPassage className="opti-quick-atc-drawer-drawer__title">
                        Choose how often you want your item
                    </TextPassage>
                    <ProductMediaBlock
                        imgSrc={product.images?.small}
                        description={`${product.name} ${product.flavor?.value ?? ''} ${product.size?.solidSize ?? ''}`}
                        priceVariant="disabled"
                        className="opti-quick-atc-drawer__item-description"
                        aria-disabled
                    />
                    <form onSubmit={handleFormSubmit} data-testid="exp-quick-add-to-cart-form">
                        <SelectField
                            className="opti-quick-atc-drawer__frequency"
                            label="Frequency"
                            data-testid="exp-quick-add-to-cart-select-frequency"
                            value={selectedFrequency}
                            items={frequencyItems}
                            onChange={onFrequencyChange}
                        />
                        <Button
                            className="opti-quick-atc-drawer__button"
                            variant="primary"
                            text="Add to cart"
                            fullWidth
                            data-testid="exp-quick-add-to-cart-button"
                        />
                    </form>
                </Drawer.Body>
            </Drawer>
        </>
    );
}
